(function () {
  'use strict';

  /*
    Autoplays the product gallery video on the PDP.

    The video is rendered inside <template> so it does not load until the
    poster is clicked (see snippets/media.liquid). On layouts where the
    gallery does not swap slides in JS -- "stacked" desktop and the
    scroll-snap mobile slider -- nothing ever clicks that poster, so the
    video stayed a poster on every viewport.

    This hydrates the video through the theme's existing DeferredMedia
    path (same code the poster click uses) once it scrolls into view, and
    pauses it again when it leaves. Playing a video that is off-screen is
    wasted bandwidth, and on stacked desktop all 7 media are in the DOM at
    once, so it is reachable without any slide change.

    Styling of the gallery is deliberately untouched: the whole reason the
    video lives in <template> is that rendering it live changed the
    measured layout. DeferredMedia already swaps it in inside the
    aspect-ratio box the poster reserved, so CLS stays at what the theme
    already achieved.
  */

  var SLIDE = '[data-type="video"]';
  var POSTER = '[data-deferred-media-button]';

  // Both DOMContentLoaded and shopify:section:load call init(), and the
  // editor fires the latter for sections already present. Track what is
  // being watched so a slide never gets two observers.
  var observed = new WeakSet();

  function play(video) {
    var promise = video.play();

    // Browsers reject play() when the document is hidden or the element is
    // off-screen. Nothing actionable, so swallow it rather than surfacing an
    // unhandled rejection.
    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {});
    }
  }

  function hydrate(deferredMedia) {
    // DeferredMedia sets a bare `loaded` attribute once it has swapped the
    // template in. getAttribute returns '' in that case, so test hasAttribute.
    if (deferredMedia.hasAttribute('loaded')) return;

    if (!deferredMedia.querySelector(POSTER)) return;
    if (typeof deferredMedia.loadContent !== 'function') return;

    // Call loadContent directly rather than dispatching a click on the
    // poster. Its first argument is `focus = true`, and a dispatched click
    // passes the Event object, which is truthy -- that would focus the
    // video and steal keyboard focus as the visitor scrolls past.
    deferredMedia.loadContent(false);
  }

  function init() {
    var slides = document.querySelectorAll(SLIDE);
    if (!slides.length) return;

    if (!('IntersectionObserver' in window)) {
      // No observer support: hydrate everything and let the browser decide.
      slides.forEach(function (slide) {
        var deferredMedia = slide.querySelector('deferred-media');
        if (!deferredMedia) return;
        hydrate(deferredMedia);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var slide = entry.target;
          var deferredMedia = slide.querySelector('deferred-media');
          if (!deferredMedia) return;

          var video = deferredMedia.querySelector('video');

          if (entry.isIntersecting) {
            if (!video) {
              hydrate(deferredMedia);
              video = deferredMedia.querySelector('video');
            }
            if (video) play(video);
          } else if (video) {
            video.pause();
          }
        });
      },
      // Start slightly before the video is fully on screen so it is already
      // running by the time it is properly in view.
      { rootMargin: '200px 0px', threshold: 0.25 }
    );

    slides.forEach(function (slide) {
      if (observed.has(slide)) return;
      observed.add(slide);
      observer.observe(slide);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify's theme editor swaps sections in and out without a reload.
  // Guarded above so re-running does not stack observers on existing slides.
  document.addEventListener('shopify:section:load', init);
})();
